/** Parents follow their children's progress; they can't grade or see other students. */
export const ROLES = ['student', 'instructor', 'owner', 'parent'] as const;
export type Role = (typeof ROLES)[number];

/**
 * Account lifecycle. Only `active` accounts may call protected endpoints.
 * `suspended` is a temporary hold, `revoked` means membership ended; both can be reactivated.
 * `deleted` accounts are anonymised and cannot be restored.
 */
export const USER_STATUSES = ['active', 'suspended', 'revoked', 'deleted'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/**
 * Students are either kids or adults; the owner chooses. Each group has its own curriculum and its
 * own leaderboards.
 */
export const AGE_GROUPS = ['adults', 'kids'] as const;
export type AgeGroup = (typeof AGE_GROUPS)[number];
export const AGE_GROUP_LABELS: Record<AgeGroup, string> = { adults: 'Adults', kids: 'Kids' };

/** Belts a student can hold. Everyone starts at white; promotions are recorded with a certificate. */
export const BELTS = ['white', 'grey-white', 'blue'] as const;
export type Belt = (typeof BELTS)[number];
export const BELT_NAMES: Record<Belt, string> = {
  white: 'White belt',
  'grey-white': 'Grey/white belt',
  blue: 'Blue belt',
};

/** Whether someone holding `held` already has `belt` (or a higher one). */
export const holdsBelt = (held: Belt | null | undefined, belt: Belt) =>
  BELTS.indexOf(held ?? 'white') >= BELTS.indexOf(belt);

/** The belt each group's curriculum leads to; the progress belt fills in its colours. */
export const NEXT_BELT: Record<AgeGroup, { name: string; belt: Exclude<Belt, 'white'> }> = {
  adults: { name: 'Blue belt', belt: 'blue' },
  kids: { name: 'Grey/white belt', belt: 'grey-white' },
};

export const APPROVAL_METHODS = ['qr', 'manual', 'override'] as const;
export type ApprovalMethod = (typeof APPROVAL_METHODS)[number];

/**
 * Technique grades, lowest first. C: started but does not know it yet; B: almost mastered, needs a
 * little work; A: has it down. Only A counts as mastered (completion, unlocking prerequisites).
 */
export const GRADES = ['C', 'B', 'A'] as const;
export type Grade = (typeof GRADES)[number];
export const MASTERY_GRADE: Grade = 'A';

export const TECHNIQUE_STATUSES = ['mastered', 'available', 'locked'] as const;
export type TechniqueStatus = (typeof TECHNIQUE_STATUSES)[number];

/** Roles allowed to sign off techniques. */
export const APPROVER_ROLES: readonly Role[] = ['instructor', 'owner'];

export function canApprove(role: Role): boolean {
  return APPROVER_ROLES.includes(role);
}

export function isOwner(role: Role): boolean {
  return role === 'owner';
}

/** Stable machine-readable error codes returned by the API (`error.code`). */
export const ERROR_CODES = [
  'VALIDATION_ERROR',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'ACCOUNT_INACTIVE',
  'INVALID_CREDENTIALS',
  'INVITATION_INVALID',
  'TOKEN_INVALID',
  'INVALID_QR',
  'STUDENT_NOT_FOUND',
  'STUDENT_INACTIVE',
  'SELF_APPROVAL',
  'TECHNIQUE_UNAVAILABLE',
  'PREREQUISITES_INCOMPLETE',
  'PREREQUISITE_CYCLE',
  'VIDEO_UNAVAILABLE',
  'SCHEMA_INCOMPATIBLE',
  'INTERNAL',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];
